import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Image,
  Button,
  HStack,
  VStack,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from "@chakra-ui/icons";
import { FaReact, FaNodeJs, FaAngular, FaJs, FaDatabase } from "react-icons/fa";
import {
  SiNetlify,
  SiFirebase,
  SiMongodb,
  SiTypescript,
  SiOpenai,
} from "react-icons/si";

const Projects = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [flippedCard, setFlippedCard] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Helper function to get tech icon
  const getTechIcon = (tech) => {
    const iconMap = {
      React: <FaReact color="#61DAFB" size={40} />,
      Netlify: <SiNetlify color="#00C7B7" size={40} />,
      "Node.js": <FaNodeJs color="#339933" size={40} />,
      MongoDB: <SiMongodb color="#47A248" size={40} />,
      TypeScript: <SiTypescript color="#3178C6" size={40} />,
      Angular: <FaAngular color="#DD0031" size={40} />,
      Firebase: <SiFirebase color="#FFCA28" size={40} />,
      JavaScript: <FaJs color="#F7DF1E" size={40} />,
      "OpenAI API": <SiOpenai color="#000000" size={40} />,
    };
    return iconMap[tech] || <FaDatabase color="#666" size={40} />;
  };

  const projects = [
    {
      id: 1,
      title: "BudgetWise",
      image: "/img/budgetWise.png",
      description:
        "Import your bank transactions and let our smart categorization do the work. Track spending across categories, set monthly budgets, and manage your finances - all with automatic balance calculations.",
      additionalInfo:
        "This is my main project - a full-stack budget tracking app with React, Node.js, and Supabase.",
      tech: ["React", "Node.js", "Firebase", "Netlify"],
      link: "https://budgetwisetracker.netlify.app/",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 2,
      title: "Moncton Services",
      image: "/img/monctonservices.png",
      description:
        "MonctonServices is a comprehensive web application designed to help residents and newcomers discover and connect with local businesses in the Greater Moncton Area (Moncton, Dieppe, and Riverview).",
      additionalInfo: "This is my biggest and most significant project.",
      tech: ["React", "Node.js", "MongoDB", "Netlify"],
      link: "https://monctonservices.netlify.app",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 3,
      title: "Perfect Booking",
      image: "/img/admin.png",
      description:
        "Appointment Booking Platform designed for small businesses across multiple industries (massage therapy, dental clinics, beauty salons).",
      additionalInfo:
        "A comprehensive booking solution for service-based businesses.",
      tech: ["React", "Node.js", "MongoDB", "Netlify"],
      link: "https://perfectbooking.netlify.app/",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 4,
      title: "FindCar",
      image: "/img/findcar.png",
      description:
        "Comprehensive solution that transforms how vehicle inspections are conducted and documented. Built with React and powered by Open AI API, this application streamlines the entire inspection workflow for professionals and agencies.",
      additionalInfo:
        "AI-powered vehicle inspection platform for professional use.",
      tech: ["React", "Node.js", "OpenAI API", "Netlify"],
      link: "https://findcaragency.netlify.app/",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 5,
      title: "Financial Tracker",
      image: "/img/financialTracker.png",
      description:
        "Financial Tracker to monitor assets, liabilities, and equity over time.",
      additionalInfo:
        "Comprehensive financial management and tracking solution.",
      tech: ["React", "Node.js", "MongoDB", "Netlify"],
      link: "https://yourfinancetotrack.netlify.app/",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 6,
      title: "Cover Letter Generator",
      image: "/img/cvgenerator.jpg",
      description:
        "It will help you very quickly to generate the Cover letter with ChatGPT, you just need to fill out simple form and get ready your Cover letter within seconds.",
      additionalInfo:
        "AI-powered cover letter generation tool for job applications.",
      tech: ["React", "Node.js", "OpenAI API", "Netlify"],
      link: "https://cvgeneratorwithai.netlify.app",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 7,
      title: "ReWritter App",
      image: "/img/reWritter.png",
      description:
        "ReWrite with AI - a tool that helps you to rewrite your sentence with AI.",
      additionalInfo: "AI-powered text rewriting tool for content enhancement.",
      tech: ["React", "Node.js", "OpenAI API", "Netlify"],
      link: "https://rewriter.netlify.app",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
    {
      id: 8,
      title: "Nyan Cat Game",
      image: "/img/nyancat.png",
      description:
        "Classic arcade-style game featuring the iconic Nyan Cat. Navigate through space collecting items and avoiding obstacles in this fun, nostalgic gaming experience.",
      additionalInfo:
        "Retro-inspired browser game with smooth animations and engaging gameplay.",
      tech: ["JavaScript", "React", "Netlify"],
      link: "https://nyancatgamesinple.netlify.app",
      githubLink: null,
      demoLink: null,
      hasInfo: true,
    },
  ];

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < projects.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleInfoClick = (projectId) => {
    setFlippedCard(flippedCard === projectId ? null : projectId);
  };

  const handleVisitSite = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX || e.touches?.[0]?.clientX || 0);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const diff = currentX - dragStart;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 100;
    if (dragOffset > threshold && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (dragOffset < -threshold && currentIndex < projects.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
    setDragOffset(0);
  };

  return (
    <Box
      as="section"
      id="projects"
      bg="#87CEEB"
      py={20}
      position="relative"
      overflow="hidden"
    >
      {/* Background decorations */}
      <Box
        position="absolute"
        top="100px"
        left="-100px"
        w="400px"
        h="400px"
        borderRadius="50%"
        border="40px solid"
        borderColor="whiteAlpha.200"
        zIndex={1}
      />
      <Box
        position="absolute"
        bottom="-150px"
        right="-150px"
        w="500px"
        h="500px"
        borderRadius="50%"
        border="50px solid"
        borderColor="whiteAlpha.200"
        zIndex={1}
      />

      <Container maxW="1200px" position="relative" zIndex={2}>
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          mb={12}
          direction={{ base: "column", md: "row" }}
          gap={{ base: 6, md: 0 }}
        >
          <Heading
            as="h2"
            fontSize={{ base: "2.5rem", md: "3rem" }}
            fontWeight="800"
            color="#2d3436"
            textAlign={{ base: "center", md: "left" }}
          >
            <Box
              as="span"
              position="relative"
              display="inline-block"
              _after={{
                content: '""',
                position: "absolute",
                bottom: "-8px",
                left: 0,
                right: 0,
                height: "16px",
                bg: "yellow.400",
                zIndex: -1,
                transform: "skewY(-2deg)",
              }}
            >
              Personal Projects
            </Box>
          </Heading>

          {/* Navigation Buttons */}
          <HStack spacing={4}>
            <IconButton
              icon={<ChevronLeftIcon boxSize={6} />}
              onClick={handlePrevious}
              isDisabled={currentIndex === 0}
              size="lg"
              borderRadius="full"
              bg={currentIndex === 0 ? "whiteAlpha.300" : "whiteAlpha.500"}
              color={currentIndex === 0 ? "gray.500" : "gray.800"}
              _hover={{
                bg: currentIndex === 0 ? "whiteAlpha.300" : "whiteAlpha.700",
                transform: currentIndex === 0 ? "none" : "translateX(-2px)",
              }}
              transition="all 0.3s"
            />
            <IconButton
              icon={<ChevronRightIcon boxSize={6} />}
              onClick={handleNext}
              isDisabled={currentIndex === projects.length - 1}
              size="lg"
              borderRadius="full"
              bg={
                currentIndex === projects.length - 1
                  ? "yellow.300"
                  : "yellow.400"
              }
              color="gray.800"
              _hover={{
                bg:
                  currentIndex === projects.length - 1
                    ? "yellow.300"
                    : "yellow.500",
                transform:
                  currentIndex === projects.length - 1
                    ? "none"
                    : "translateX(2px)",
              }}
              transition="all 0.3s"
            />
          </HStack>
        </Flex>

        {/* Carousel Container */}
        <Box
          position="relative"
          h={{ base: "550px", md: "650px" }}
          py={10}
          cursor={isDragging ? "grabbing" : "grab"}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          userSelect="none"
        >
          <Flex position="absolute" inset={0} align="center" justify="center">
            {projects.map((project, index) => {
              const offset = index - currentIndex;
              const isActive = index === currentIndex;

              // Card positioning - responsive card width
              const cardWidth = isMobile ? 320 : 700; // Responsive width
              const cardGap = isMobile ? 10 : 5; // Responsive gap
              const baseTranslate = offset * (cardWidth + cardGap);
              const dragTranslate = dragOffset;
              const totalTranslate = baseTranslate + dragTranslate;

              // Scale and opacity based on position
              const scale = isActive ? 1 : 0.85;
              const opacity =
                Math.abs(offset) === 0 ? 1 : Math.abs(offset) === 1 ? 0.7 : 0.3;
              const blur =
                Math.abs(offset) === 0 ? 0 : Math.abs(offset) === 1 ? 2 : 5;

              const isFlipped = flippedCard === project.id;

              return (
                <Box
                  key={project.id}
                  position="absolute"
                  w={{ base: "320px", md: "700px" }}
                  transform={`translateX(${totalTranslate}px) scale(${scale})`}
                  opacity={opacity}
                  filter={`blur(${blur}px)`}
                  zIndex={projects.length - Math.abs(offset)}
                  transition={
                    isDragging
                      ? "none"
                      : "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                  }
                  pointerEvents={isActive ? "auto" : "none"}
                  style={{ perspective: "1000px" }}
                >
                  {/* Card container with flip animation */}
                  <Box position="relative" w="100%" h={{ base: "500px", md: "600px" }}>
                    {/* Front card */}
                    <Box
                      position="absolute"
                      w="100%"
                      h={{ base: "500px", md: "600px" }}
                      transform={isFlipped ? "scaleX(0)" : "scaleX(1)"}
                      opacity={isFlipped ? 0 : 1}
                      transition="all 0.4s ease-in-out"
                    >
                      {/* Front of Card */}
                      <Box
                        bg="white"
                        borderRadius="25px"
                        overflow="hidden"
                        boxShadow="0 20px 60px rgba(0, 0, 0, 0.1)"
                        w="100%"
                        h="100%"
                        display="flex"
                        flexDirection="column"
                      >
                        {/* Title at top with decorative dots */}
                        <HStack
                          justify="space-between"
                          align="center"
                          p={{ base: 4, md: 6 }}
                          pb={{ base: 2, md: 4 }}
                        >
                          <Heading
                            as="h3"
                            fontSize={{ base: "1.2rem", md: "1.5rem" }}
                            fontWeight="700"
                            color="gray.800"
                          >
                            <HStack spacing={2}>
                              <Box
                                w="8px"
                                h="8px"
                                borderRadius="full"
                                bg="#5fc4b8"
                              />
                              <Box
                                w="8px"
                                h="8px"
                                borderRadius="full"
                                bg="#FFA500"
                              />
                              <Text ml={2}>{project.title}</Text>
                            </HStack>
                          </Heading>
                        </HStack>

                        {/* Project Image - Takes most of the space */}
                        <Box
                          flex={1}
                          position="relative"
                          bg="linear-gradient(135deg, rgba(91, 192, 235, 0.1) 0%, rgba(105, 199, 188, 0.1) 100%)"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          overflow="hidden"
                        >
                          {/* Decorative circles in background */}
                          <Box
                            position="absolute"
                            top="10%"
                            left="10%"
                            w="200px"
                            h="200px"
                            borderRadius="50%"
                            border="20px solid"
                            borderColor="rgba(91, 192, 235, 0.2)"
                          />

                          <Image
                            src={project.image}
                            alt={project.title}
                            w="100%"
                            h="100%"
                            objectFit="contain"
                            position="relative"
                            zIndex={2}
                          />
                        </Box>

                        {/* Tech Stack Icons at bottom */}
                        <HStack
                          spacing={{ base: 3, md: 6 }}
                          p={{ base: 4, md: 6 }}
                          justify="center"
                          bg="white"
                          borderTop="1px solid"
                          borderColor="gray.200"
                          minH={{ base: "60px", md: "80px" }}
                          flexWrap="wrap"
                        >
                          {project.tech.slice(0, 4).map((tech, idx) => (
                            <Box
                              key={idx}
                              w={{ base: "40px", md: "60px" }}
                              h={{ base: "40px", md: "60px" }}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              transition="transform 0.2s"
                              _hover={{ transform: "scale(1.1)" }}
                            >
                              {React.cloneElement(getTechIcon(tech), {
                                size: isMobile ? 30 : 40
                              })}
                            </Box>
                          ))}
                        </HStack>

                        {/* Info Button */}
                        {isActive && project.hasInfo && (
                          <IconButton
                            icon={<InfoIcon />}
                            onClick={() => handleInfoClick(project.id)}
                            position="absolute"
                            bottom={{ base: "60px", md: "80px" }}
                            right={{ base: "15px", md: "20px" }}
                            size={{ base: "md", md: "lg" }}
                            borderRadius="full"
                            bg="#5BC0EB"
                            color="white"
                            _hover={{ bg: "#4aa8d8", transform: "scale(1.1)" }}
                            boxShadow="lg"
                            zIndex={5}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Back card */}
                    <Box
                      position="absolute"
                      w="100%"
                      h={{ base: "500px", md: "600px" }}
                      transform={!isFlipped ? "scaleX(0)" : "scaleX(1)"}
                      opacity={!isFlipped ? 0 : 1}
                      transition="all 0.4s ease-in-out"
                      transitionDelay={isFlipped ? "0.4s" : "0s"}
                    >
                      {/* Back of Card */}
                      <Box
                        bg="#2d3436"
                        borderRadius="25px"
                        overflow="hidden"
                        boxShadow="0 20px 60px rgba(0, 0, 0, 0.1)"
                        position="absolute"
                        w="100%"
                        h="100%"
                        display="flex"
                        flexDirection="column"
                        p={{ base: 6, md: 12 }}
                        justifyContent="center"
                      >
                        {/* Close button */}
                        <Box position="absolute" top={{ base: 4, md: 6 }} right={{ base: 4, md: 6 }}>
                          <IconButton
                            icon={
                              <Box fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
                                ×
                              </Box>
                            }
                            onClick={() => handleInfoClick(project.id)}
                            size={{ base: "md", md: "lg" }}
                            borderRadius="full"
                            bg="transparent"
                            color="white"
                            border="2px solid white"
                            _hover={{ bg: "whiteAlpha.200" }}
                          />
                        </Box>

                        {/* Content */}
                        <VStack spacing={{ base: 4, md: 6 }} align="stretch" justify="center">
                          <Text
                            fontSize={{ base: "1rem", md: "1.3rem" }}
                            color="white"
                            lineHeight="1.8"
                          >
                            {project.description}
                          </Text>

                          <Text
                            fontSize={{ base: "1rem", md: "1.3rem" }}
                            color="white"
                            lineHeight="1.8"
                          >
                            <Text as="span" color="#5BC0EB" fontWeight="700">
                              {project.additionalInfo}
                            </Text>
                          </Text>

                          {/* Tech icons at bottom */}
                          <HStack
                            spacing={{ base: 4, md: 6 }}
                            justify="flex-start"
                            pt={2}
                            flexWrap="wrap"
                          >
                            {project.tech.slice(0, 4).map((tech, idx) => (
                              <Box
                                key={idx}
                                w={{ base: "40px", md: "50px" }}
                                h={{ base: "40px", md: "50px" }}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                {React.cloneElement(getTechIcon(tech), {
                                  size: isMobile ? 30 : 40
                                })}
                              </Box>
                            ))}
                          </HStack>

                          {/* Buttons */}
                          <HStack spacing={4} pt={4} justify={{ base: "center", md: "flex-start" }}>
                            <Button
                              onClick={() => handleVisitSite(project.link)}
                              variant="outline"
                              borderColor="#5BC0EB"
                              color="#5BC0EB"
                              size={{ base: "md", md: "lg" }}
                              borderWidth="2px"
                              borderRadius="12px"
                              fontWeight="600"
                              px={{ base: 6, md: 8 }}
                              fontSize={{ base: "sm", md: "md" }}
                              _hover={{
                                bg: "rgba(91, 192, 235, 0.1)",
                                transform: "translateY(-2px)",
                              }}
                            >
                              Live
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Flex>
        </Box>

        {/* Dots Indicator */}
        <Flex justify="center" gap={3} mt={8}>
          {projects.map((_, index) => (
            <Button
              key={index}
              onClick={() => setCurrentIndex(index)}
              w={index === currentIndex ? "40px" : "12px"}
              h="12px"
              minW="auto"
              p={0}
              bg={index === currentIndex ? "gray.800" : "whiteAlpha.600"}
              borderRadius="full"
              _hover={{
                bg: index === currentIndex ? "gray.800" : "whiteAlpha.800",
              }}
              transition="all 0.3s"
            />
          ))}
        </Flex>
      </Container>
    </Box>
  );
};

export default Projects;
